/**
 * Leviathan - Terminal Form Page
 * Quasar Prime LLC
 *
 * Create and edit fuel terminals
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { parseApiError } from '@/utils/errorHandler';
import {
  ArrowLeft,
  Save,
  Loader2,
  AlertTriangle,
} from 'lucide-react';

import { API_URL } from '@/config/api';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

interface TerminalFormData {
  terminalCode: string;
  name: string;
  displayName: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  county: string;
  phone: string;
  fax: string;
  email: string;
  tcn: string;
  fein: string;
  stateLicenseNumber: string;
  taxJurisdictionId: string;
  operator: string;
  supplierId: string;
  operatingHours: string;
  isActive: boolean;
  notes: string;
}

const defaultFormData: TerminalFormData = {
  terminalCode: '',
  name: '',
  displayName: '',
  address: '',
  city: '',
  state: 'TX',
  zip: '',
  county: '',
  phone: '',
  fax: '',
  email: '',
  tcn: '',
  fein: '',
  stateLicenseNumber: '',
  taxJurisdictionId: '',
  operator: '',
  supplierId: '',
  operatingHours: '',
  isActive: true,
  notes: '',
};

export default function TerminalForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const isEdit = Boolean(id);

  /**
   * Configured tax jurisdictions, so the terminal can be pinned to one.
   * Loaded rather than typed: the code has to MATCH an existing jurisdiction or
   * origin resolution finds nothing.
   */
  const [jurisdictions, setJurisdictions] = useState<
    { id: string; code: string; name: string; level: string }[]
  >([]);

  const [formData, setFormData] = useState<TerminalFormData>(defaultFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch existing terminal for edit
  const { isLoading: isLoadingTerminal } = useQuery({
    queryKey: ['terminal', id],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/terminals/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch terminal');
      return res.json();
    },
    enabled: isEdit,
  });

  // Update form when terminal data is loaded
  useEffect(() => {
    if (isEdit && !isLoadingTerminal) {
      // Use a separate query to get the data
    }
  }, [isEdit, isLoadingTerminal]);

  // Fetch terminal data separately for initial load
  useEffect(() => {
    if (isEdit) {
      fetch(`${API_URL}/api/terminals/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
        .then((res) => res.json())
        .then((data) => {
          setFormData({
            terminalCode: data.terminalCode || '',
            name: data.name || '',
            displayName: data.displayName || '',
            address: data.address || '',
            city: data.city || '',
            state: data.state || 'TX',
            taxJurisdictionId: data.taxJurisdictionId || '',
            zip: data.zip || '',
            county: data.county || '',
            phone: data.phone || '',
            fax: data.fax || '',
            email: data.email || '',
            tcn: data.tcn || '',
            fein: data.fein || '',
            stateLicenseNumber: data.stateLicenseNumber || '',
            operator: data.operator || '',
            supplierId: data.supplierId || '',
            operatingHours: data.operatingHours || '',
            isActive: data.isActive ?? true,
            notes: data.notes || '',
          });
        })
        .catch((err) => console.error('Failed to load terminal:', err));
    }
  }, [id, isEdit, accessToken]);

  // Fetch suppliers for dropdown
  const { data: suppliersData } = useQuery({
    queryKey: ['suppliers'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/suppliers?limit=100`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch suppliers');
      return res.json();
    },
  });

  const suppliers = suppliersData?.items || [];

  // Configured jurisdictions, for the tax-jurisdiction picker.
  useEffect(() => {
    fetch(`${API_URL}/api/tax/config/jurisdictions`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((res) => (res.ok ? res.json() : { jurisdictions: [] }))
      .then((data) => setJurisdictions(data.jurisdictions ?? []))
      .catch(() => setJurisdictions([]));
  }, [accessToken]);

  /**
   * The jurisdiction the terminal's STATE would resolve to if nothing is pinned.
   *
   * This is the distinction the previous session asked for: a terminal with a
   * state match is KNOWN AND RESOLVABLE — leaving it unset is fine. A terminal
   * with neither a pin nor a match is KNOWN BUT UNMAPPED, which is a
   * CONFIGURATION gap the operator can close in a minute, and reads differently
   * from "we don't know where this fuel came from".
   */
  const stateMatch = jurisdictions.find(
    (j) => j.code === formData.state && j.level === 'STATE'
  );

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data: TerminalFormData) => {
      const url = isEdit ? `${API_URL}/api/terminals/${id}` : `${API_URL}/api/terminals`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          supplierId: data.supplierId || null,
        }),
      });
      if (!res.ok) {
        const errorMessage = await parseApiError(res);
        throw new Error(errorMessage);
      }
      return res.json();
    },
    onSuccess: (data) => {
      navigate(`/terminals/${data.id}`);
    },
  });

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.terminalCode.trim()) {
      newErrors.terminalCode = 'Terminal code is required';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Terminal name is required';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.state) {
      newErrors.state = 'State is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    saveMutation.mutate(formData);
  };

  const updateField = (field: keyof TerminalFormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (isEdit && isLoadingTerminal) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to={isEdit ? `/terminals/${id}` : '/terminals'}
          className="p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-slate-100">
            {isEdit ? 'Edit Terminal' : 'New Terminal'}
          </h1>
          <p className="text-slate-400">
            {isEdit ? 'Update terminal information' : 'Add a new fuel terminal'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Terminal Identification */}
        <Panel title="Terminal Identification">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <label className="block text-slate-400 text-sm mb-2">Terminal Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                placeholder="e.g., Marathon Pasadena Terminal"
              />
              {errors.name && <p className="text-red-400 text-sm mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-slate-400 text-sm mb-2">Code *</label>
              <input
                type="text"
                value={formData.terminalCode}
                onChange={(e) => updateField('terminalCode', e.target.value.toUpperCase())}
                className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 uppercase focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                placeholder="MARPAS"
                maxLength={10}
              />
              {errors.terminalCode && (
                <p className="text-red-400 text-sm mt-1">{errors.terminalCode}</p>
              )}
            </div>

            <div>
              <label className="block text-slate-400 text-sm mb-2">Display Name</label>
              <input
                type="text"
                value={formData.displayName}
                onChange={(e) => updateField('displayName', e.target.value)}
                className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                placeholder="Short display name"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-slate-400 text-sm mb-2">
                Terminal Control Number (TCN)
              </label>
              <input
                type="text"
                value={formData.tcn}
                onChange={(e) => updateField('tcn', e.target.value)}
                className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                placeholder="T-76-TX-2830"
              />
              <p className="text-slate-500 text-xs mt-1">
                IRS terminal number for tax reporting (T-XX-ST-XXXX)
              </p>
            </div>
          </div>
        </Panel>

        {/* Location */}
        <Panel title="Location">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-slate-400 text-sm mb-2">Address</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => updateField('address', e.target.value)}
                className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>

            <div>
              <label className="block text-slate-400 text-sm mb-2">City *</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => updateField('city', e.target.value)}
                className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
              {errors.city && <p className="text-red-400 text-sm mt-1">{errors.city}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-slate-400 text-sm mb-2">State *</label>
                <select
                  value={formData.state}
                  onChange={(e) => updateField('state', e.target.value)}
                  className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                >
                  {US_STATES.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-2">ZIP</label>
                <input
                  type="text"
                  value={formData.zip}
                  onChange={(e) => updateField('zip', e.target.value)}
                  className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                  maxLength={10}
                />
              </div>
            </div>

            <div>
              <label className="block text-slate-400 text-sm mb-2">County</label>
              <input
                type="text"
                value={formData.county}
                onChange={(e) => updateField('county', e.target.value)}
                className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
          </div>
        </Panel>

        {/* Operator & Supplier */}
        <Panel title="Terminal Operator">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-slate-400 text-sm mb-2">Operator Name</label>
              <input
                type="text"
                value={formData.operator}
                onChange={(e) => updateField('operator', e.target.value)}
                className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                placeholder="e.g., Kinder Morgan"
              />
            </div>

            <div>
              <label className="block text-slate-400 text-sm mb-2">Supplier</label>
              <select
                value={formData.supplierId}
                onChange={(e) => updateField('supplierId', e.target.value)}
                className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              >
                <option value="">Select supplier...</option>
                {suppliers.map((s: { id: string; name: string }) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-slate-400 text-sm mb-2">Operator FEIN</label>
              <input
                type="text"
                value={formData.fein}
                onChange={(e) => updateField('fein', e.target.value)}
                className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                placeholder="XX-XXXXXXX"
              />
            </div>

            <div>
              <label className="block text-slate-400 text-sm mb-2">State License #</label>
              <input
                type="text"
                value={formData.stateLicenseNumber}
                onChange={(e) => updateField('stateLicenseNumber', e.target.value)}
                className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>

            {/* TAX JURISDICTION.
                Fuel lifted here has this terminal as its ORIGIN, and tax
                liability follows the movement — a load lifted in Texas and
                delivered to Louisiana owes Louisiana. If the terminal cannot be
                resolved to a jurisdiction, the engine falls back to assuming
                the load never crossed a state line.

                Note this is a CONFIGURATION gap and reports differently from an
                unknown origin: the fix is "map this terminal", not "record
                where this came from". */}
            <div className="md:col-span-2">
              <label className="block text-slate-400 text-sm mb-2">Tax Jurisdiction</label>
              <select
                value={formData.taxJurisdictionId}
                onChange={(e) => updateField('taxJurisdictionId', e.target.value)}
                className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              >
                <option value="">
                  {stateMatch
                    ? `Match by state — ${stateMatch.name} (${stateMatch.code})`
                    : '— Not set —'}
                </option>
                {jurisdictions.map((jurisdiction) => (
                  <option key={jurisdiction.id} value={jurisdiction.id}>
                    {jurisdiction.name} ({jurisdiction.code}) · {jurisdiction.level}
                  </option>
                ))}
              </select>

              {formData.taxJurisdictionId ? (
                <p className="mt-1 text-xs text-slate-500">
                  Fuel lifted here is taxed as originating in this jurisdiction.
                </p>
              ) : stateMatch ? (
                <p className="mt-1 text-xs text-slate-500">
                  Left unset, origin resolves by matching the state code{' '}
                  <span className="text-slate-300">{formData.state}</span> to{' '}
                  <span className="text-slate-300">{stateMatch.name}</span>. Set it explicitly if
                  this rack sits in a county or city with its own fuel tax — a state match will
                  silently miss those.
                </p>
              ) : (
                <div className="mt-2 flex items-start gap-2 border border-red-500/50 bg-red-500/10 p-3">
                  <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-slate-300">
                    <strong className="text-red-400">No jurisdiction, and no match for state “{formData.state}”.</strong>{' '}
                    Fuel lifted here has a known origin that cannot be resolved, so tax will be
                    calculated <em>assuming the load never left the delivery state</em>. Create a
                    jurisdiction with code <span className="font-mono">{formData.state}</span>, or
                    pick one above.
                  </p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-slate-400 text-sm mb-2">Operating Hours</label>
              <input
                type="text"
                value={formData.operatingHours}
                onChange={(e) => updateField('operatingHours', e.target.value)}
                className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                placeholder="e.g., 24/7 or Mon-Fri 6am-6pm"
              />
            </div>
          </div>
        </Panel>

        {/* Contact */}
        <Panel title="Contact Information">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-slate-400 text-sm mb-2">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => updateField('phone', e.target.value)}
                className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>

            <div>
              <label className="block text-slate-400 text-sm mb-2">Fax</label>
              <input
                type="tel"
                value={formData.fax}
                onChange={(e) => updateField('fax', e.target.value)}
                className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>

            <div>
              <label className="block text-slate-400 text-sm mb-2">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => updateField('email', e.target.value)}
                className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
          </div>
        </Panel>

        {/* Notes */}
        <Panel title="Notes">
          <textarea
            value={formData.notes}
            onChange={(e) => updateField('notes', e.target.value)}
            rows={4}
            className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50 resize-none"
            placeholder="Additional notes about this terminal..."
          />
        </Panel>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
          <Link
            to={isEdit ? `/terminals/${id}` : '/terminals'}
            className="px-6 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-100 rounded-lg font-medium transition-colors"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={saveMutation.isPending}
            className="flex items-center gap-2 px-6 py-2.5 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {saveMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {saveMutation.isPending ? 'Saving...' : isEdit ? 'Update Terminal' : 'Create Terminal'}
          </button>
        </div>

        {saveMutation.isError && (
          <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400">
            {saveMutation.error instanceof Error
              ? saveMutation.error.message
              : 'Failed to save terminal'}
          </div>
        )}
      </form>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
      <h3 className="text-lg font-semibold text-slate-100 mb-6 pb-3 border-b border-slate-700">
        {title}
      </h3>
      {children}
    </div>
  );
}
